import semver from "semver";
import {DeviceScheduleStatus} from "@barix/rpp-types";


class DeviceCommonUtils {


    public static removeVersionSuffix(version: string): string    {
        return version.replace(/[-+].*/gi, "");
    }


    /**
     * Check if device firmware supports time zone
     * @param installedFirmwareVersion
     */
    public static deviceSupportsTimeZone(installedFirmwareVersion: string): boolean {
        if (installedFirmwareVersion) {
            return semver.satisfies(DeviceCommonUtils.removeVersionSuffix(installedFirmwareVersion), ">=5.2.1");
        }
        return false;
    }


    /**
     * Check if device firmware supports channels schedule (v1)
     * @param installedFirmwareVersion
     */
    public static deviceSupportsChannelScheduleV1(installedFirmwareVersion: string): boolean {
        if (installedFirmwareVersion) {
            return semver.satisfies(DeviceCommonUtils.removeVersionSuffix(installedFirmwareVersion), ">=5.4.0");
        }
        return false;
    }


    /**
     * Check if device firmware supports automation schedule (v1)
     * @param installedFirmwareVersion
     */
    public static deviceSupportsAssetsSchedule(installedFirmwareVersion: string): boolean {
        return DeviceCommonUtils.deviceSupportsApiV2(installedFirmwareVersion);
    }


    /**
     * Check if device firmware supports advanced asset schedule (v2)
     * @param installedFirmwareVersion
     */
    public static deviceSupportsAssetsScheduleV2(installedFirmwareVersion: string): boolean {
        if (installedFirmwareVersion) {
            return semver.satisfies(DeviceCommonUtils.removeVersionSuffix(installedFirmwareVersion), ">=6.5.0");
        }
        return false;
    }


    /**
     * Determines if the device supports API v2
     * @param installedFirmwareVersion
     * @returns {boolean}
     */
    public static deviceSupportsApiV2(installedFirmwareVersion: string): boolean {
        if (installedFirmwareVersion) {
            return semver.satisfies(DeviceCommonUtils.removeVersionSuffix(installedFirmwareVersion), ">=6.0.0");
        }
        return false;
    }


    /**
     * Determines if the device supports API v3
     * @param installedFirmwareVersion
     * @returns {boolean}
     */
    public static deviceSupportsApiV3(installedFirmwareVersion: string): boolean {
        if (installedFirmwareVersion) {
            return semver.satisfies(DeviceCommonUtils.removeVersionSuffix(installedFirmwareVersion), ">=6.5.0");
        }
        return false;
    }


    /**
     * Determines if the device supports API v4
     * @param installedFirmwareVersion
     * @returns {boolean}
     */
    public static deviceSupportsApiV4(installedFirmwareVersion: string): boolean {
        if (installedFirmwareVersion) {
            return semver.satisfies(DeviceCommonUtils.removeVersionSuffix(installedFirmwareVersion), ">=6.6.0");
        }
        return false;
    }


    /**
     * Determines if the device supports API v5
     * @param installedFirmwareVersion
     * @returns {boolean}
     */
    public static deviceSupportsApiV5(installedFirmwareVersion: string): boolean {
        if (installedFirmwareVersion) {
            return semver.satisfies(DeviceCommonUtils.removeVersionSuffix(installedFirmwareVersion), ">=6.8.0");
        }
        return false;
    }


    /**
     * Get device API level in order to firmware version.
     * @param installedFirmwareVersion
     * @returns {boolean}
     */
    public static getDeviceAPILevel(installedFirmwareVersion: string): number {
        if (installedFirmwareVersion) {
            if (DeviceCommonUtils.deviceSupportsApiV5(installedFirmwareVersion)) {
                return 5;
            } else {
                if (DeviceCommonUtils.deviceSupportsApiV4(installedFirmwareVersion)) {
                    return 4;
                } else {
                    if (DeviceCommonUtils.deviceSupportsApiV3(installedFirmwareVersion)) {
                        return 3;
                    } else {
                        if (DeviceCommonUtils.deviceSupportsApiV2(installedFirmwareVersion)) {
                            return 2;
                        } else {
                            return 1;
                        }
                    }
                }
            }
        }
        return 0;
    }


    /**
     * Determines if the device supports API v2
     * @param installedFirmwareVersion
     * @returns {boolean}
     */
    public static deviceSupportsAssetTriggers(installedFirmwareVersion: string): boolean {
        if (installedFirmwareVersion) {
            return semver.satisfies(DeviceCommonUtils.removeVersionSuffix(installedFirmwareVersion), ">=6.1.0");
        }
        return false;
    }


    /**
     * Devices versions before 5.2.2 does not support huge fallbackTrack lists
     * @returns {boolean} device version support huge fallbackTrack lists
     */
    public static deviceSupportHugeFallbackTrackList(installedFirmwareVersion: string): boolean {
        if (installedFirmwareVersion) {
            return semver.satisfies(DeviceCommonUtils.removeVersionSuffix(installedFirmwareVersion), ">=5.2.2");
        }
        return false;
    }


    public static isOnline(device: { online: boolean } | undefined): boolean {
        return device ? device.online : false;
    }


    public static getOnlineLabelKey(device: { online: boolean } | undefined): string {
        return DeviceCommonUtils.isOnline(device) ? "Online" : "Offline";
    }


    public static getDurationExpressionFromSeconds(seconds: number | string): string | null {
        const secs = Number(seconds);
        if (secs < 0) {
            return null;
        }
        const d = Math.floor(secs / 86400);
        const h = Math.floor(secs % 86400 / 3600);
        const m = Math.floor(secs % 86400 % 3600 / 60);
        // const s = Math.floor(secs % 86400 % 3600 % 60);
        const dStr = d > 0 ? d + "d" : "";
        const hStr = h > 0 ? " " + h + "h" : "";
        const mStr = " " + m + "m";
        // const mStr = m > 0 ? " " + m + " m" : "";
        // const sStr = s > 0 ? " " + s + " s" : "";
        return dStr + hStr + mStr;
    }


    public static isScheduleActive(device: { status: { scheduleStatus: DeviceScheduleStatus } | undefined }): boolean {
        // return device && device.status.scheduleStatus && device.status.scheduleStatus === "Active";
        if (device && device.status) {
            return device.status.scheduleStatus === "Active";
        }
        return false;
    }


    public static isMuted(device: { muted: boolean }): boolean {
        if (!device)    {
            return true;
        } else  {
            return device.muted;
        }
    }


    public static canPlay(device: { online: boolean, muted: boolean, status: { scheduleStatus: DeviceScheduleStatus } }): boolean {
        return !DeviceCommonUtils.isMuted(device) && DeviceCommonUtils.isOnline(device) && DeviceCommonUtils.isScheduleActive(device);
    }

}

export default DeviceCommonUtils;
